import { put } from '../http/serviceLayerClient';
import { viewGet } from '../http/viewProxyClient';

/**
 * Rep Preferences API — reads via view proxy, writes via SL.
 */

/**
 * Tristate weight literal per SL PR #1282 SL-RPB2 (mig 045_rep_preferences_tristate.sql).
 * Commander 2026-05-20: "NEUTRAL MUST BE IN" — all three literals required.
 * The mission generator multiplies its scoring by these weights at build time.
 */
export type RepWeightLiteral = 'overweight' | 'neutral' | 'underweight';

/** Subset of RepPreferences written via the dedicated weights endpoint. */
export interface RepPreferencesWeights {
  /** Map of product SKU/slug → tristate. Empty object clears all overrides. */
  product_weights: Record<string, RepWeightLiteral>;
  /** Map of NACE code → tristate. Empty object clears all overrides. */
  industry_weights: Record<string, RepWeightLiteral>;
}

export interface RepPreferences {
  id: string;
  user_email: string;
  joker_max_per_day: number;
  focus_industries: string[];
  focus_products: string[];
  focus_plz: string[];
  /**
   * Tristate weights (SL PR #1282 SL-RPB2). Optional because pre-mig-045
   * rows may not have these columns populated; consumers default to {}.
   */
  product_weights?: Record<string, RepWeightLiteral>;
  industry_weights?: Record<string, RepWeightLiteral>;
  global_business_focus: Record<string, unknown>;
  selected_bestandskunden: unknown[];
  updated_at: string;
  updated_by: string | null;
}

/**
 * Get preferences for a specific rep by email.
 * Returns null if no preferences row exists yet.
 *
 * GET /v1/rep-preferences/{email}
 */
export async function getRepPreferences(email: string): Promise<RepPreferences | null> {
  try {
    const res = await viewGet<RepPreferences>('rep-preferences', {
      filters: { user_email: email },
      limit: 1,
    });
    return res.data[0] ?? null;
  } catch (error: any) {
    if (error?.status === 404) return null;
    console.error('[getRepPreferences] Error:', error);
    throw error;
  }
}

/**
 * Upsert preferences for a rep.
 *
 * PUT /v1/rep-preferences/{email}
 */
export async function updateRepPreferences(
  email: string,
  updates: Partial<Omit<RepPreferences, 'id' | 'user_email'>>,
): Promise<RepPreferences> {
  const payload: Partial<RepPreferences> = {
    user_email: email,
    ...updates,
    updated_at: new Date().toISOString(),
  };

  const res = await put<{ success: boolean; data: RepPreferences }>(
    `/v1/rep-preferences/${encodeURIComponent(email)}`,
    payload,
  );
  const body = res.data as any;
  const prefs = (body?.data ?? body) as RepPreferences | null;

  if (!prefs) {
    throw new Error('[updateRepPreferences] Service Layer returned empty response');
  }

  return prefs;
}

/**
 * Update only the tristate `product_weights` + `industry_weights` jsonb
 * columns via the dedicated SL endpoint (SL PR #1282 SL-RPB2).
 *
 * PUT /v1/rep-preferences/{email}/weights
 *
 * Caller authorization (SL-enforced):
 *  - manager / sales_manager / superadmin → may edit any rep.
 *  - other roles → may only edit their own rep_email (path param must
 *    match the caller's JWT email).
 *
 * Errors (SL envelope `{ detail: { error: { code, message } } }`):
 *  - 403 FORBIDDEN            — non-elevated caller editing different rep
 *  - 404 REP_NOT_FOUND        — no rep_preferences row for email
 *  - 422 INVALID_WEIGHT_VALUE — value outside the tristate (pydantic Literal)
 *  - 422 INVALID_WEIGHT_KEY   — empty / non-string key
 *  - 500 UPDATE_FAILED        — DB exception
 *  - 502 UPDATE_FAILED        — read-back verify drift (request ≠ persisted)
 *
 * Send `{ product_weights: {}, industry_weights: {} }` to clear both maps.
 */
export async function updateRepPreferencesWeights(
  email: string,
  weights: RepPreferencesWeights,
): Promise<RepPreferences> {
  const res = await put<{ success: boolean; data: RepPreferences }>(
    `/v1/rep-preferences/${encodeURIComponent(email)}/weights`,
    weights,
  );
  const body = res.data as any;
  const prefs = (body?.data ?? body) as RepPreferences | null;

  if (!prefs) {
    throw new Error('[updateRepPreferencesWeights] Service Layer returned empty response');
  }

  return prefs;
}

/**
 * List all rep preferences (Admin only).
 *
 * GET /v1/rep-preferences
 */
export async function listAllRepPreferences(): Promise<RepPreferences[]> {
  // FAIL-LOUD (FR-FE-CODEGEN-FAIL-LOUD-DEFENSIVE-READ-STD): a fetch failure
  // propagates so the admin caller renders an Error state. A genuine 200 with
  // no rows returns []. We never catch-and-return-[] (that masked a failed
  // read as "no rep preferences" — the all-dark / silent-missing-data class).
  const res = await viewGet<RepPreferences>('rep-preferences');
  return res.data;
}
